// src/pages/MoneyMovement.js
//
// Chunk 7 — Money Movement (spec §9). Advice, not accounting.
// Tells the user "$X needs to be in [account] by [date]" based purely on
// upcoming bill obligations (next 30 days). It does NOT track transfers, sync
// with any bank, or read account balances — it only reads bills that have a
// "Pays from" account assigned and are due soon.
//
import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useDataEvent, EVENTS } from '../lib/dataEvents'
import { usePartner } from '../lib/usePartner'
import { useCurrency } from '../lib/CurrencyContext'
import { theme } from '../lib/theme'
import { buildPaidMonthSet, resolveUnpaidOccurrence } from '../lib/moneyMovement'

const c = theme.colors
const f = theme.fonts

const HORIZON_DAYS = 30

function dayLabel(date) {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

export default function MoneyMovement() {
  const { partnerId, loading: partnerLoading } = usePartner()
  const { formatCurrency } = useCurrency()
  const [bills, setBills] = useState([])
  const [accounts, setAccounts] = useState([])
  const [paidMonthSet, setPaidMonthSet] = useState(new Set())
  const [loading, setLoading] = useState(true)
  const today = useMemo(() => new Date(), [])

  useEffect(() => { if (!partnerLoading) fetchAll() /* eslint-disable-next-line */ }, [partnerId, partnerLoading])
  useDataEvent(
    [EVENTS.BILL_SAVED, EVENTS.BILL_UPDATED, EVENTS.BILL_DELETED, EVENTS.ACCOUNT_SAVED, EVENTS.ACCOUNT_UPDATED, EVENTS.ACCOUNT_DELETED],
    () => fetchAll()
  )

  async function fetchAll() {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]
    // Matched-payment window: current month + next 3 (covers the 30-day horizon).
    const now = new Date()
    const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
    const end = new Date(now.getFullYear(), now.getMonth() + 4, 1)
    const mmEnd = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-01`
    const [billsRes, acctsRes, mmPaidRes] = await Promise.all([
      supabase.from('bills').select('*').in('user_id', userIds).eq('is_active', true).order('due_day'),
      supabase.from('accounts').select('id, name').in('user_id', userIds).order('name'),
      supabase.from('transactions').select('bill_id, transaction_date')
        .in('user_id', userIds).not('bill_id', 'is', null)
        .gte('transaction_date', monthStart).lt('transaction_date', mmEnd),
    ])
    setBills(billsRes.data || [])
    setAccounts(acctsRes.data || [])
    setPaidMonthSet(buildPaidMonthSet(mmPaidRes.data || []))
    setLoading(false)
  }

  const acctById = useMemo(() => {
    const m = {}
    accounts.forEach(a => { m[a.id] = a })
    return m
  }, [accounts])

  // Bills with a "Pays from" account. Each is resolved to its earliest UNPAID
  // occurrence within the horizon (paid periods are skipped — Chunk 7 follow-up);
  // bills with no unpaid occurrence in the window drop out.
  const assigned = useMemo(() => bills.filter(b => !!b.account_id), [bills])
  const upcoming = useMemo(() => {
    return assigned
      .map(b => ({ ...b, _occ: resolveUnpaidOccurrence(b, today, HORIZON_DAYS, paidMonthSet) }))
      .filter(b => b._occ)
  }, [assigned, today, paidMonthSet])

  // Per-account 30-day summary.
  const byAccount = useMemo(() => {
    const groups = {}
    upcoming.forEach(b => {
      const g = groups[b.account_id] || (groups[b.account_id] = {
        accountId: b.account_id,
        name: acctById[b.account_id]?.name || 'account',
        total: 0, count: 0, earliest: Infinity, earliestDate: null,
      })
      g.total += parseFloat(b.amount || 0)
      g.count += 1
      if (b._occ.offset < g.earliest) { g.earliest = b._occ.offset; g.earliestDate = b._occ.date }
    })
    return Object.values(groups).sort((a, b) => a.earliest - b.earliest)
  }, [upcoming, acctById])

  // Day-by-day schedule: each day with bills → accounts → bills.
  const schedule = useMemo(() => {
    const days = {}
    upcoming.forEach(b => {
      const key = b._occ.offset
      const day = (days[key] = days[key] || { date: b._occ.date, accounts: {} })
      const acct = (day.accounts[b.account_id] = day.accounts[b.account_id] || {
        accountId: b.account_id,
        name: acctById[b.account_id]?.name || 'account',
        total: 0, bills: [],
      })
      acct.total += parseFloat(b.amount || 0)
      acct.bills.push(b)
    })
    return Object.keys(days)
      .map(Number)
      .sort((a, b) => a - b)
      .map(offset => ({
        offset,
        date: days[offset].date,
        accounts: Object.values(days[offset].accounts).sort((a, b) => a.name.localeCompare(b.name)),
      }))
  }, [upcoming, acctById])

  if (loading) {
    return <div style={styles.loading}>Loading…</div>
  }

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>Money Movement</h1>
        <p style={styles.tagline}>Where your dollars need to be, and when.</p>
      </header>

      {assigned.length === 0 ? (
        <div style={styles.empty}>
          <h2 style={styles.emptyTitle}>Nothing to move yet</h2>
          <p style={styles.emptyText}>
            To use Money Movement, edit your bills and pick a “Pays from” account
            for each one that comes out of a specific account. We’ll tell you what
            to transfer where, and by when.
          </p>
          <Link to="/plan" style={styles.emptyCta}>Go to Plan → Bills</Link>
        </div>
      ) : upcoming.length === 0 ? (
        <div style={styles.empty}>
          <h2 style={styles.emptyTitle}>All clear for the next 30 days</h2>
          <p style={styles.emptyText}>
            None of your account-assigned bills are due in the next 30 days.
          </p>
        </div>
      ) : (
        <>
          {/* By-account summary */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>By account · next 30 days</h2>
            <div style={styles.summaryGrid}>
              {byAccount.map(g => (
                <div key={g.accountId} style={styles.summaryCard}>
                  <div style={styles.summaryName}>{g.name}</div>
                  <div style={styles.summaryTotal}>{formatCurrency(g.total)}</div>
                  <div style={styles.summaryMeta}>
                    {g.count} bill{g.count === 1 ? '' : 's'} · first due {dayLabel(g.earliestDate)}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Day-by-day schedule */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Transfer schedule</h2>
            <div style={styles.schedule}>
              {schedule.map(day => (
                <div key={day.offset} style={styles.dayBlock}>
                  <div style={styles.dayDate}>{dayLabel(day.date)}</div>
                  <div style={styles.dayAccounts}>
                    {day.accounts.map(acct => (
                      <div key={acct.accountId} style={styles.dayAccount}>
                        <div style={styles.dayAccountHead}>
                          <span style={styles.dayAccountName}>{acct.name}</span>
                          <span style={styles.dayAccountTotal}>{formatCurrency(acct.total)}</span>
                        </div>
                        <div style={styles.dayBills}>
                          {acct.bills.map(b => (
                            <span key={b.id} style={styles.dayBillChip}>
                              {b.name} · {formatCurrency(b.amount)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      <footer style={styles.footer}>
        <p style={styles.footerText}>
          Advice only — Vault tells you what to transfer, it doesn’t move money or
          track your account balances. Make transfers in your bank app.
        </p>
      </footer>
    </div>
  )
}

const styles = {
  page: { padding: '24px', maxWidth: '900px', margin: '0 auto' },
  loading: { padding: '40px', textAlign: 'center', color: c.inkMuted },
  header: { marginBottom: '24px' },
  title: {
    fontFamily: f.display, fontSize: '32px', fontWeight: 600,
    color: c.plum, margin: 0, letterSpacing: '-0.02em',
  },
  tagline: { color: c.inkMuted, fontSize: '14px', margin: '6px 0 0' },

  empty: {
    padding: '48px 24px', textAlign: 'center', background: c.cream2,
    borderRadius: '16px', border: `1px solid ${c.line}`,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
  },
  emptyTitle: { fontSize: '20px', fontWeight: 600, color: c.plum, margin: 0 },
  emptyText: { color: c.inkMuted, fontSize: '14px', maxWidth: '420px', lineHeight: 1.55, margin: 0 },
  emptyCta: {
    marginTop: '4px', padding: '10px 18px', borderRadius: '10px',
    background: c.plum, color: c.cream, textDecoration: 'none',
    fontFamily: f.body, fontSize: '14px', fontWeight: 600,
  },

  section: { marginBottom: '28px' },
  sectionTitle: {
    fontFamily: f.display, fontSize: '18px', fontWeight: 600,
    color: c.plum, margin: '0 0 12px', letterSpacing: '-0.01em',
  },

  summaryGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px',
  },
  summaryCard: {
    background: c.white, borderRadius: '12px', padding: '16px',
    border: `1px solid ${c.line}`, boxShadow: theme.shadows.card,
  },
  summaryName: { fontSize: '13px', fontWeight: 600, color: c.plumSoft },
  summaryTotal: {
    fontFamily: f.display, fontSize: '24px', fontWeight: 600, color: c.plum,
    margin: '4px 0 2px', fontVariantNumeric: 'tabular-nums',
  },
  summaryMeta: { fontSize: '12px', color: c.inkMuted },

  schedule: { display: 'flex', flexDirection: 'column', gap: '10px' },
  dayBlock: {
    background: c.white, borderRadius: '12px', padding: '14px 16px',
    border: `1px solid ${c.line}`,
  },
  dayDate: {
    fontSize: '13px', fontWeight: 700, color: c.plum,
    marginBottom: '10px', letterSpacing: '0.01em',
  },
  dayAccounts: { display: 'flex', flexDirection: 'column', gap: '12px' },
  dayAccount: {},
  dayAccountHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: '12px', marginBottom: '6px',
  },
  dayAccountName: { fontSize: '13.5px', fontWeight: 600, color: c.plumSoft },
  dayAccountTotal: {
    fontSize: '14px', fontWeight: 700, color: c.plum, fontVariantNumeric: 'tabular-nums',
  },
  dayBills: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  dayBillChip: {
    background: c.cream2, color: c.plumSoft, borderRadius: '7px',
    padding: '4px 9px', fontSize: '12.5px', fontWeight: 500, fontVariantNumeric: 'tabular-nums',
  },

  footer: { marginTop: '8px', paddingTop: '16px', borderTop: `1px solid ${c.line}` },
  footerText: { fontSize: '12px', color: c.inkMuted, lineHeight: 1.5, margin: 0 },
}
